import { MPUT } from 'common/helpers.ts';
import { useCallback, useMemo } from 'react';
import useSWRMutation from 'swr/mutation';
import { useMe } from './useMe.tsx';

const DATE_FORMATS = ['DD/MM/YYYY HH:mm', 'MM/DD/YYYY HH:mm', 'YYYY/MM/DD HH:mm'] as const;
const DATE_ONLY_FORMATS = ['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY/MM/DD'] as const;

export type DateFormat = (typeof DATE_FORMATS)[number] | (typeof DATE_ONLY_FORMATS)[number];

export const useDateFormat = () => {
  // Fetch current user data including preferred_date_format
  const { me: user, mutate: mutateUser } = useMe();

  // Mutation for updating user data
  const { trigger: updateUser, isMutating: isUpdatingUser } = useSWRMutation(`/user/${user?.id}`, MPUT);

  const currentDateFormat = useMemo((): DateFormat => {
    if (!user?.preferred_date_format) {
      return DATE_FORMATS[0];
    }

    // Validate that the stored format is in our allowed formats
    if (!DATE_FORMATS.includes(user.preferred_date_format as (typeof DATE_FORMATS)[number])) {
      return DATE_FORMATS[0];
    }

    return user.preferred_date_format as DateFormat;
  }, [user?.preferred_date_format]);

  const updateDateFormat = useCallback(
    async (newFormat: DateFormat) => {
      if (!user?.id) {
        throw new Error('User not loaded');
      }

      try {
        await updateUser({ preferred_date_format: newFormat });
        await mutateUser();
      } catch (error) {
        console.error('Failed to update date format:', error);
        throw error;
      }
    },
    [updateUser, mutateUser, user?.id],
  );

  const currentDateOnlyFormat = useMemo((): DateFormat => currentDateFormat.split(' ')[0] as DateFormat, [currentDateFormat]);

  const dateFormats = useMemo(() => [...DATE_FORMATS], []);

  return {
    currentDateFormat,
    currentDateOnlyFormat,
    dateFormats,
    updateDateFormat,
    isLoading: !user,
    isUpdatingUser,
  };
};
